import React, { useState, useEffect } from "react";
import styles from "./BaseAddressInput.module.scss";
import classNames from "classnames/bind";
import { MapPinIcon } from "~/assets/icons";

const cx = classNames.bind(styles);

interface BaseAddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect?: (suggestion: any) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  inputClassName?: string;
  errorMessage?: string;
}

const BaseAddressInput: React.FC<BaseAddressInputProps> = ({
  value,
  onChange,
  onSuggestionSelect,
  placeholder = "Enter full address",
  label = "Address",
  className,
  inputClassName,
  errorMessage,
}) => {
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [selectedAddressSuggestion, setSelectedAddressSuggestion] = useState<any>(null);
  const [isAddressInputFocused, setIsAddressInputFocused] = useState<boolean>(false);
  const [typingTimeout, setTypingTimeout] = useState<any>(null);

  const GOOGLE_PLACES_API_KEY = "AIzaSyBFvJr6l0XfUv2axQAvHaHnTzwzwlKJ1oo";

  const fetchAddressSuggestions = async (query: string) => {
    if (!query || !GOOGLE_PLACES_API_KEY) {
      setAddressSuggestions([]);
      return;
    }

    try {
      const res = await fetch(
        "https://places.googleapis.com/v1/places:autocomplete",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": GOOGLE_PLACES_API_KEY,
          },
          body: JSON.stringify({ input: query }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error("Places API error:", res.status, errorData);
        setAddressSuggestions([]);
        return;
      }

      const data = await res.json();
      setAddressSuggestions(data.suggestions || []);
    } catch (err) {
      console.error("Places API fetch error:", err);
      setAddressSuggestions([]);
    }
  };

  useEffect(() => {
    if (value && isAddressInputFocused) {
      if (typingTimeout) clearTimeout(typingTimeout);

      const timeout = setTimeout(() => {
        fetchAddressSuggestions(value);
      }, 500);

      setTypingTimeout(timeout);
    } else if (!isAddressInputFocused) {
      setAddressSuggestions([]);
    }

    return () => {
      if (typingTimeout) clearTimeout(typingTimeout);
    };
  }, [value, isAddressInputFocused, typingTimeout]);

  const handleAddressSuggestionClick = (location: any) => {
    setSelectedAddressSuggestion(location);
    const addressText = location?.placePrediction?.text?.text || "";
    onChange(addressText);
    
    if (onSuggestionSelect) {
      onSuggestionSelect(location);
    }
    
    setAddressSuggestions([]);
    setIsAddressInputFocused(false);
  };

  return (
    <div className={cx("contact-type", className)}>
      {label && <h3 className={cx("title")}>{label}</h3>}

      <div className={cx("search-box", { error: errorMessage }, inputClassName)}>
        <input
          type="text"
          placeholder={placeholder}
          className={cx("input")}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setSelectedAddressSuggestion(null);
          }}
          onFocus={() => {
            setIsAddressInputFocused(true);
            if (value) {
              fetchAddressSuggestions(value);
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              setIsAddressInputFocused(false);
            }, 200);
          }}
        />
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cx("search-icon")}
        >
          <path
            d="M12.75 12.75L9.85 9.85M11.4167 6.08333C11.4167 9.02885 9.02885 11.4167 6.08333 11.4167C3.13781 11.4167 0.75 9.02885 0.75 6.08333C0.75 3.13781 3.13781 0.75 6.08333 0.75C9.02885 0.75 11.4167 3.13781 11.4167 6.08333Z"
            stroke="#5D5F61"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {errorMessage && (
        <div className={cx("error-message")}>{errorMessage}</div>
      )}

      {isAddressInputFocused && addressSuggestions.length > 0 && (
        <div className={cx("suggestions-container")}>
          {addressSuggestions.map((suggestion, index) => (
            <div
              className={cx(
                "map-preview",
                selectedAddressSuggestion === suggestion && "active"
              )}
              key={index}
              onClick={() => handleAddressSuggestionClick(suggestion)}
            >
              <div className={cx("map-info")}>
                <MapPinIcon className={cx("map-icon")} />
              </div>
              <div className={cx("map-address-container")}>
                <p className={cx("map-address")}>
                  {suggestion?.placePrediction?.text?.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BaseAddressInput;

